import React, { useEffect, useMemo, useState } from 'react';
import IsoDatePicker from './IsoDatePicker';
import PSInlinePicker from './PSInlinePicker';
import CBInlinePicker from './CBInlinePicker';
import MSInlinePicker from './MSInlinePicker';
import ACInlinePicker from './ACInlinePicker';
import { psSlotsLocal, cbSessionsLocal, acAvailabilityLocal } from '../utils/availabilityLocal';
import { msSlotsClient } from '../utils/msClient';

// Helper: check if date (YYYY-MM-DD) lies within [start, start+windowDays-1]
function isWithinWindow(dateISO, startISO, windowDays) {
  if (!dateISO || !startISO || !windowDays) return false;
  const d = new Date(dateISO);
  const start = new Date(startISO);
  const end = new Date(start);
  end.setDate(end.getDate() + Math.max(1, Number(windowDays)) - 1);
  return d >= start && d <= end;
}

// Picker selection will call onPick(offering, selection)
export default function BundleInlinePicker({
  offering,
  onPick, // parent expects onPick(offering, pick)
  bookingRange,
  hoursCtx,
  msRules,
  live,
  cbSessions,
  psSchedule,
  employeesIndex = {},
  acSeasons,
  acModifiers,
  psEmpWork,
  psOffDays,
  psScheduleMode,
  psActivePeriods,
  psEquipPools,
  psEquipUsage,
  timezone,
  firstBookableISO,
  offeringsIndex = {},
  defaultDate = '',
  winStart = '',
  winEnd = '',
  initialParty = 2,
  hideAdd = false,
}) {
  // Business default: bundles must be consumed within 2 days unless configured otherwise
  const windowDays = Math.max(1, Number(offering.consumption_window_days || 2));
  const [bundleStartDate, setBundleStartDate] = useState('');
  const [adding, setAdding] = useState(false);
  // selections keyed by offering.id -> { offering, selection, date }
  const [itemSelections, setItemSelections] = useState({});
  const [computedStarts, setComputedStarts] = useState([]);
  const [computing, setComputing] = useState(false);
  const [itemParties, setItemParties] = useState({}); // { [offeringId]: party }

  // Resolve each bundle item to an actual offering object we already render in the lists
  const bundleItems = useMemo(() => {
    const items = Array.isArray(offering.items) ? offering.items : [];
    return items.map((it) => {
      let off = null;
      if (it.mode === 'personal_services') {
        off = Object.values(offeringsIndex).find(o => o.kind === 'ps_service' && String(o.id) === String(it.item_id));
      } else if (it.mode === 'class_based') {
        off = Object.values(offeringsIndex).find(o => o.kind === 'cb_class' && String(o.id) === String(it.item_id));
      } else if (it.mode === 'modular_seating') {
        // item_id may be table_type_id or seating_config_id — try both
        off = Object.values(offeringsIndex).find(o => o.kind === 'ms_table_type' && (String(o.id) === String(it.item_id) || String(o.ms_seating_config_id) === String(it.item_id)));
      } else if (it.mode === 'accommodation') {
        off = Object.values(offeringsIndex).find(o => o.kind === 'ac_room_type' && String(o.id) === String(it.item_id));
      }
      return off ? { ...it, offering: off } : null;
    }).filter(Boolean);
  }, [offering.items, offeringsIndex]);

  // Seed default party per item when bundleItems change
  useEffect(() => {
    setItemParties(prev => {
      const next = { ...prev };
      for (const it of bundleItems) {
        const id = it.offering.id;
        if (next[id] == null) next[id] = Number(initialParty || 1);
      }
      // drop removed ones
      Object.keys(next).forEach(k => {
        if (!bundleItems.some(it => String(it.offering.id) === String(k))) delete next[k];
      });
      return next;
    });
  }, [bundleItems.map(it => it.offering.id).join('|'), initialParty]);

  function onPartyChange(offId, value) {
    const v = Math.max(1, Number(value || 1));
    setItemParties(p => ({ ...p, [offId]: v }));
  }

  // Prefer precomputed dates from offering._availableDates; fallback empty
  const enabledBundleDates = useMemo(() => (
    (Array.isArray(offering._availableDates) && offering._availableDates.length)
      ? offering._availableDates
      : (computedStarts || [])
  ), [offering._availableDates, computedStarts]);

  // Compute bundle valid start dates locally when not provided
  useEffect(() => {
    let cancelled = false;
    async function computeStarts() {
      if (!bookingRange?.start_date || !bookingRange?.end_date) { setComputedStarts([]); return; }
      // If backend already provided dates, honor them
      if (Array.isArray(offering._availableDates) && offering._availableDates.length) { setComputedStarts(offering._availableDates); return; }

      const items = bundleItems; // already resolved to offerings
      if (!items.length) { setComputedStarts([]); return; }

      setComputing(true);
      try {
        const start = new Date(bookingRange.start_date);
        const end   = new Date(bookingRange.end_date);

        const within = (t) => {
          if (!t) return false;
          if (winStart && t < winStart) return false;
          if (winEnd && t > winEnd) return false;
          return true;
        };

  const tz = timezone;

        // Helper: does an item have at least one bookable time inside [s, s+windowDays-1]?
        function itemOKWithinWindow(item, startISO) {
          const off = item.offering;
          const party = Number(itemParties[off.id] || 1);
          for (let i = 0; i < windowDays; i++) {
            const d = new Date(startISO);
            d.setDate(d.getDate() + i);
            const dayISO = d.toISOString().slice(0,10);

            if (off.kind === 'ps_service') {
              const employees = off._employees || employeesIndex[off.id] || [];
              const r = psSlotsLocal({
                dateISO: dayISO,
                serviceId: off.id,
                durationMin: off.duration_min,
                hoursCtx,
                psSchedule,
                empWork: psEmpWork,
                empOff: psOffDays,
                employees,
                tz,
                firstBookableISO,
                live,
                scheduleModeByService: psScheduleMode,
                party,
                activePeriods: psActivePeriods,
                equipPools: psEquipPools,
                equipUsage: psEquipUsage,
              });
              // require there are at least `party` simultaneous seats at some time
              let any = false;
              const counts = new Map();
              (r?.available || []).forEach(g => (g.slots || []).forEach(s => {
                if (within(s.start) && within(s.end)) counts.set(s.start, (counts.get(s.start) || 0) + 1);
              }));
              any = Array.from(counts.values()).some(cnt => cnt >= Math.max(1, party));
              if (any) return true;
            } else if (off.kind === 'cb_class') {
              const r = cbSessionsLocal({ dateISO: dayISO, classId: off.id, capacity: off.capacity, cbSessions, tz, firstBookableISO, live });
              const any = (r?.sessions || []).some(s => within(s.start_time) && Number(s.remaining || 0) >= Math.max(1, party));
              if (any) return true;
            } else if (off.kind === 'ms_table_type') {
              const slots = msSlotsClient({ dateISO: dayISO, party, seatingConfigId: off.ms_seating_config_id, areaType: off.ms_area_type, hoursCtx, msRules, live, firstBookableISO, tz });
              const any = (slots || []).some(s => within(s.start) && within(s.end));
              if (any) return true;
            } else if (off.kind === 'ac_room_type') {
              // AC: require at least one night overlapping within the window
              const d2 = new Date(dayISO); d2.setDate(d2.getDate() + 1);
              const nextISO = d2.toISOString().slice(0,10);
              const r = acAvailabilityLocal({ roomTypeId: off.id, startDate: dayISO, endDate: nextISO, quantity: off.quantity, seasons: acSeasons, live });
              const capOK = !Number(off.max_guests) || Number(off.max_guests) >= Math.max(1, party);
              if (capOK && (r?.available || 0) > 0) return true;
            }
          }
          return false;
        }

        const valids = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const sISO = d.toISOString().slice(0,10);
          let ok = true;
          for (const it of items) {
            if (!itemOKWithinWindow(it, sISO)) { ok = false; break; }
          }
          if (ok) valids.push(sISO);
          if (valids.length > 730) break; // safe cap
        }
        if (!cancelled) setComputedStarts(valids);
      } finally {
        if (!cancelled) setComputing(false);
      }
    }
    computeStarts();
    return () => { cancelled = true; };
  }, [
    bookingRange?.start_date,
    bookingRange?.end_date,
    winStart,
    winEnd,
    windowDays,
    // parties
    Object.entries(itemParties).map(([k,v]) => `${k}:${v}`).join('|'),
    // bundle items & essential contexts
    JSON.stringify((offering.items || []).map(it => ({ mode: it.mode, item_id: it.item_id }))),
    timezone,
    firstBookableISO,
    live,
    hoursCtx,
    msRules,
    cbSessions,
    acSeasons,
    psSchedule,
    (psEmpWork||[]).length,
    (psOffDays||[]).length,
    (psActivePeriods||[]).length,
    (psEquipPools||[]).length,
    (psEquipUsage||[]).length,
  ]);

  // Helper: for AC selections, consider overlap with the bundle window
  function acIntersectsWindow(acStart, acEnd, startISO, days) {
    if (!acStart || !acEnd || !startISO || !days) return false;
    const winStart = new Date(startISO);
    const winEnd = new Date(winStart);
    winEnd.setDate(winEnd.getDate() + Math.max(1, Number(days)) - 1);

    const s = new Date(acStart);
    // AC end is checkout; nights cover up to the day before checkout
    const e = new Date(acEnd);
    e.setDate(e.getDate() - 1);
    for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
      if (d >= winStart && d <= winEnd) return true;
    }
    return false;
  }

  // Keep selections only if within current bundle window
  useEffect(() => {
    if (!bundleStartDate) return;
    setItemSelections(prev => {
      const next = { ...prev };
      for (const [offId, sel] of Object.entries(prev)) {
        const item = bundleItems.find(bi => String(bi.offering.id) === String(offId));
        const kind = item?.offering?.kind;
        if (kind === 'ac_room_type') {
          const acStart = sel?.selection?.guests_json?.ac_start_date || sel?.selection?.ac_start_date;
          const acEnd   = sel?.selection?.guests_json?.ac_end_date || sel?.selection?.ac_end_date;
          if (!acIntersectsWindow(acStart, acEnd, bundleStartDate, windowDays)) delete next[offId];
        } else {
          const dayISO = sel?.date || sel?.selection?.slot_start?.slice(0, 10) || sel?.selection?.class_session_date || '';
          if (!isWithinWindow(dayISO, bundleStartDate, windowDays)) delete next[offId];
        }
      }
      return next;
    });
  }, [bundleStartDate, windowDays, bundleItems]);

  function handleItemSelection(off, selection) {
    const date = selection?.slot_start
      ? String(selection.slot_start).slice(0, 10)
      : (selection?.ac_start_date || selection?.class_session_date || selection?.date || '');
    setItemSelections(prev => ({
      ...prev,
      [off.id]: { offering: off, selection, date }
    }));
  }
  // Draft handler: treat valid drafts as selections; null clears
  function handleItemDraft(off, selectionOrNull) {
    if (!selectionOrNull) {
      setItemSelections(prev => {
        const next = { ...prev };
        delete next[off.id];
        return next;
      });
      return;
    }
    handleItemSelection(off, selectionOrNull);
  }
  const allItemsSelectedWithinWindow = useMemo(() => {
    if (!bundleStartDate) return false;
    if (!bundleItems.length) return false;
    return bundleItems.every(it => {
      const sel = itemSelections[it.offering.id];
      if (!sel) return false;
      if (it.offering.kind === 'ac_room_type') {
        const acStart = sel?.selection?.guests_json?.ac_start_date || sel?.selection?.ac_start_date;
        const acEnd   = sel?.selection?.guests_json?.ac_end_date || sel?.selection?.ac_end_date;
        return acIntersectsWindow(acStart, acEnd, bundleStartDate, windowDays);
      }
      const dayISO = sel?.date || '';
      return dayISO && isWithinWindow(dayISO, bundleStartDate, windowDays);
    });
  }, [itemSelections, bundleItems, bundleStartDate, windowDays]);

  async function addBundleToCart() {
    if (!bundleStartDate || !allItemsSelectedWithinWindow) return;
    setAdding(true);
    try {
      // Build bundle_items payload for server
      const items = bundleItems.map(it => {
        const sel = itemSelections[it.offering.id]?.selection || {};
        const common = { mode: it.mode, item_id: it.item_id, party_size: Math.max(1, Number(sel.party || initialParty || 1)) };
        if (it.mode === 'personal_services') {
          return {
            ...common,
            slot_start_local: sel.slot_start,
            slot_end_local: sel.slot_end,
            employee_id: sel.employee_id || null,
          };
        }
        if (it.mode === 'class_based') {
          return {
            ...common,
            class_session_id: sel.class_session_id,
            class_session_date: sel.class_session_date || (sel.date || ''),
          };
        }
        if (it.mode === 'modular_seating') {
          return {
            ...common,
            ms_seating_config_id: sel.ms_seating_config_id || sel.guests_json?.ms_seating_config_id,
            ms_area_type: sel.ms_area_type || sel.guests_json?.ms_area_type,
            slot_start_local: sel.slot_start,
            slot_end_local: sel.slot_end,
          };
        }
        if (it.mode === 'accommodation') {
          return {
            ...common,
            ac_start_date: sel.guests_json?.ac_start_date || sel.ac_start_date,
            ac_end_date: sel.guests_json?.ac_end_date || sel.ac_end_date,
          };
        }
        return common;
      });

      // Compute overall bundle party size from per-item party sizes
      const overallParty = Math.max(1, ...(items.map(it => Number(it.party_size || 1))));
      // Delegate to parent add-to-cart handler
      await onPick(offering, {
        slot_start: null,
        slot_end: null,
        party: overallParty,
        guests_json: {
          party_size: overallParty,
          bundle_start_date: bundleStartDate,
          bundle_items: items,
        }
      });
    } finally {
      setAdding(false);
    }
  }
  return (
    <div className="space-y-4">

      {/* Pickers per item */}
      <div className="grid gap-3">
        {bundleItems.map((it) => {
          const off = it.offering;
          const key = `${it.mode}-${it.item_id}`;
          const kind = off.kind;
          const commonProps = {
            bookingRange,
            hoursCtx,
            msRules,
            live,
            cbSessions,
            acSeasons,
            acModifiers,
            psEmpWork,
            psOffDays,
            psScheduleMode,
            psActivePeriods,
            psEquipPools,
            psEquipUsage,
            timezone,
            firstBookableTs: firstBookableISO,
            winStart,
            winEnd,
            defaultDate,
            initialParty: '1',
            hideAdd: true,
          };

          return (
            <div key={key} className="border rounded-lg p-3 bg-white">
              <div className="text-sm font-medium mb-2">
                {it.title || off.title}
                <span className="text-xs text-gray-500 ml-2">({off.kind_label || it.mode})</span>
              </div>
              {kind === 'ps_service' && (
                (() => {
                    const props = {
                    key: `${key}-ps-${initialParty}`,
                    offering: off,
                    employees: off._employees || employeesIndex[off.id] || [],
                    onPick: handleItemSelection,
                    onDraftChange: handleItemDraft,
                    psSchedule,
                    ...commonProps,
                    initialParty: String(itemParties[off.id] ?? Number(initialParty || 1)),
                    onPartyChangeExternal: (v) => onPartyChange(off.id, v),
                    caller: `BundleInlinePicker:item-${it.item_id}`,
                  };
                  return <PSInlinePicker {...props} />;
                })()
              )}
              {kind === 'cb_class' && (
                <CBInlinePicker
                  key={`${key}-cb-${initialParty}`}
                  offering={off}
                  onPick={handleItemSelection}
                  onDraftChange={handleItemDraft}
                  employeesIndex={employeesIndex}
                  initialParty={String(itemParties[off.id] ?? Number(initialParty || 1))}
                  {...commonProps}
                />
              )}
              {kind === 'ms_table_type' && (
                <MSInlinePicker
                  key={`${key}-ms-${initialParty}`}
                  offering={off}
                  onPick={handleItemSelection}
                  onDraftChange={handleItemDraft}
                  initialParty={String(itemParties[off.id] ?? Number(initialParty || 1))}
                  {...commonProps}
                />
              )}
              {kind === 'ac_room_type' && (
                <ACInlinePicker
                  key={`${key}-ac-${initialParty}`}
                  offering={off}
                  onPick={handleItemSelection}
                  onDraftChange={handleItemDraft}
                  initialParty={String(itemParties[off.id] ?? Number(initialParty || 1))}
                  {...commonProps}
                />
              )}
            </div>
          );
        })}
        {!bundleItems.length && (
          <div className="text-xs text-amber-700">Bundle has no items configured.</div>
        )}
      </div>

      {/* Bundle start date based on availability window */}
      <div>
        <label className="block text-sm font-medium">Bundle start</label>
        <IsoDatePicker
          value={bundleStartDate}
          onChange={setBundleStartDate}
          minDate={bookingRange?.start_date}
          maxDate={bookingRange?.end_date}
          includeDates={enabledBundleDates}
          includeDatesStrict
          placeholderText={enabledBundleDates?.length ? 'Select date' : 'No valid dates'}
        />
        {bundleStartDate && (
          <div className="text-xs text-gray-600 mt-1">
            Window: {bundleStartDate} → {(() => { const d = new Date(bundleStartDate); d.setDate(d.getDate()+windowDays-1); return d.toISOString().slice(0,10); })()}
          </div>
        )}
        {!enabledBundleDates?.length && (
          <div className="text-xs text-amber-700 mt-1">No dates where all items are available within the window.</div>
        )}
      </div>

      {/* Add to cart */}
      {!hideAdd && (
        <button
          disabled={!bundleStartDate || !allItemsSelectedWithinWindow || adding}
          onClick={addBundleToCart}
          className="w-full py-2 rounded bg-black text-white disabled:opacity-40"
        >
          {adding ? 'Adding…' : 'Add Bundle to cart'}
        </button>
      )}

      {bundleStartDate && (
        <div className="text-[11px] text-gray-600">
          Selected {Object.keys(itemSelections).length}/{bundleItems.length} items within window.
        </div>
      )}
    </div>
  );
}
